import React, { useMemo, useState } from "react";
import {
  View,
  Text,
  Modal,
  Pressable,
  ScrollView,
  TextInput,
  StyleSheet,
} from "react-native";
import { Ionicons, Feather } from "@expo/vector-icons";
import AdminCheckbox from "../../../components/AdminCheckbox";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../config/theme/adminColors";

const labelOf = (item) => (item?.title ? item.title : item?.name) || "";
const mpOf = (item) => (item?.marketplace ? item.marketplace : "STRIPE");
// Mesma regra de tipo das telas admin (EditProfile/DataTable): plano/assinatura
// vs curso vs produto avulso — mostrado ao lado do marketplace para o professor
// saber O QUE está concedendo.
const typeOf = (item) => {
  const mp = mpOf(item);
  const isSubscription =
    item?.type === "plan" ||
    mp === "Plano" ||
    (mp === "IAP" && /monthly|quarterly|semiannual|yearly/i.test(item?.productId || ""));
  if (isSubscription) return "Assinatura";
  if (item?.type === "course" || mp === "Curso") return "Curso";
  return "Produto";
};

const FlatListMultipleSelection = ({ items = [], setItems, smallText, footerText }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = items.filter((i) => i.selected);

  const toggle = (target, value) => {
    setItems(items.map((i) => (i === target ? { ...i, selected: value } : i)));
  };

  const sorted = useMemo(() => {
    const arr = [...items].sort((a, b) => labelOf(a).localeCompare(labelOf(b), "pt-BR"));
    const q = query.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (i) =>
        labelOf(i).toLowerCase().includes(q) ||
        mpOf(i).toLowerCase().includes(q) ||
        typeOf(i).toLowerCase().includes(q)
    );
  }, [items, query]);

  return (
    <View style={{ width: "100%" }}>
      {/* Gatilho estilo select */}
      <Pressable
        onPress={() => setOpen(true)}
        style={({ hovered }) => [styles.trigger, hovered && { borderColor: ADMIN_COLORS.accent }]}
      >
        <Text
          numberOfLines={1}
          style={[styles.triggerText, selected.length === 0 && { color: ADMIN_COLORS.placeholder }]}
        >
          {selected.length > 0
            ? `${selected.length} produto(s) selecionado(s)`
            : footerText || "Selecione os produtos"}
        </Text>
        <Feather name="chevron-down" size={18} color={ADMIN_COLORS.muted} />
      </Pressable>

      {/* Chips dos selecionados */}
      {selected.length > 0 ? (
        <View style={{ marginTop: 10 }}>
          {smallText ? <Text style={styles.chipsLabel}>{smallText}</Text> : null}
          <View style={styles.chipsWrap}>
            {selected.map((item, idx) => (
              <View key={`${labelOf(item)}-${idx}`} style={styles.chip}>
                <Text style={styles.chipText} numberOfLines={1}>
                  {labelOf(item)}
                </Text>
                <Pressable onPress={() => toggle(item, false)} hitSlop={6} style={styles.chipClose}>
                  <Feather name="x" size={13} color={ADMIN_COLORS.muted} />
                </Pressable>
              </View>
            ))}
          </View>
        </View>
      ) : null}

      {/* Modal de seleção */}
      <Modal animationType="fade" transparent visible={open} onRequestClose={() => setOpen(false)}>
        <View style={styles.overlay}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Selecione os produtos</Text>
              <Pressable onPress={() => setOpen(false)} hitSlop={8} style={styles.iconBtn}>
                <Ionicons name="close" size={20} color={ADMIN_COLORS.ink} />
              </Pressable>
            </View>

            <View style={styles.searchWrap}>
              <Ionicons name="search" size={16} color={ADMIN_COLORS.muted} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Buscar produto..."
                placeholderTextColor={ADMIN_COLORS.placeholder}
                style={styles.searchInput}
              />
            </View>

            <ScrollView style={{ maxHeight: 360 }} contentContainerStyle={{ paddingVertical: 4 }}>
              {sorted.length === 0 ? (
                <Text style={styles.empty}>Nenhum produto encontrado.</Text>
              ) : (
                sorted.map((item, idx) => (
                  <AdminCheckbox
                    key={`${labelOf(item)}-${idx}`}
                    value={!!item.selected}
                    onValueChange={(v) => toggle(item, v)}
                    style={styles.optionRow}
                  >
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.optionLabel} numberOfLines={2}>
                        {labelOf(item)}
                      </Text>
                      <Text style={styles.optionMp}>{mpOf(item)} · {typeOf(item)}</Text>
                    </View>
                  </AdminCheckbox>
                ))
              )}
            </ScrollView>

            <Pressable
              onPress={() => setOpen(false)}
              style={({ hovered }) => [styles.doneBtn, hovered && { backgroundColor: ADMIN_COLORS.accentDark }]}
            >
              <Text style={styles.doneBtnText}>
                Concluir{selected.length > 0 ? ` (${selected.length})` : ""}
              </Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  trigger: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: "pointer",
  },
  triggerText: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.ink,
  },
  chipsLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: "600",
    color: ADMIN_COLORS.muted,
    marginBottom: 6,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    maxWidth: "100%",
    backgroundColor: ADMIN_COLORS.accentSoft,
    borderRadius: 999,
    paddingLeft: 12,
    paddingRight: 8,
    paddingVertical: 6,
  },
  chipText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "500",
    color: ADMIN_COLORS.accentDark,
    flexShrink: 1,
  },
  chipClose: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  overlay: {
    flex: 1,
    backgroundColor: "rgba(17,24,39,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modalCard: {
    width: "100%",
    maxWidth: 480,
    backgroundColor: ADMIN_COLORS.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    padding: 16,
    boxShadow: "0 12px 32px rgba(16,24,40,0.18)",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  modalTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 16,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  searchWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    height: 40,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    marginBottom: 8,
  },
  searchInput: {
    flex: 1,
    height: "100%",
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.ink,
    backgroundColor: "transparent",
    borderWidth: 0,
    outlineStyle: "none",
  },
  empty: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    textAlign: "center",
    paddingVertical: 24,
  },
  optionRow: {
    alignItems: "flex-start",
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  optionLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.ink,
  },
  optionMp: {
    fontFamily: ADMIN_FONT,
    fontSize: 11,
    color: ADMIN_COLORS.muted,
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  // Estes dois estilos eram REFERENCIADOS mas não existiam no StyleSheet —
  // o botão "Concluir" renderizava como uma barra azul crua, fora do padrão.
  doneBtn: {
    marginTop: 12,
    minHeight: 44,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    backgroundColor: ADMIN_COLORS.accent,
    cursor: "pointer",
  },
  doneBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default FlatListMultipleSelection;
